import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import RatingModal from '../../components/shared/RatingModal';
import ChatButton from '../../components/shared/ChatButton';
import EscrowStatus from '../../components/shared/EscrowStatus';
import DisputeModal from '../../components/shared/DisputeModal';

const STATUS_COLORS = {
  completed:        { bg: '#eaf4ef', color: '#2d6a4f', label: 'Entregado' },
  in_delivery:      { bg: '#fef3e2', color: '#b45309', label: 'En tránsito' },
  queued_offline:   { bg: '#fef3e2', color: '#b45309', label: '⚡ Cola offline' },
  pending_payment:  { bg: '#eaf1fb', color: '#1a4e8c', label: 'Pago pendiente' },
  payment_received: { bg: '#eaf4ef', color: '#1a7a4a', label: 'Pago confirmado' },
  kyc_review:       { bg: '#f5f0ff', color: '#6b21a8', label: 'En revisión' },
  failed:           { bg: '#faeef4', color: '#9b2c5e', label: 'Fallido' },
  refunded:         { bg: '#f0ebe2', color: '#7a7060', label: 'Reembolsado' },
};

const FILTER_LABELS = {
  all:              'Todas',
  completed:        'Entregadas',
  in_delivery:      'En tránsito',
  pending_payment:  'Pago pendiente',
  payment_received: 'Pago confirmado',
  failed:           'Fallidas',
};

const DELIVERY_LABELS = {
  cash:          'Efectivo',
  transfermovil: 'Transfermóvil',
  cubacel:       'Cubacel',
};

function StarDisplay({ rating, size = 14 }) {
  if (!rating) return null;
  return (
    <span style={{ color: '#f59e0b', fontSize: size }}>
      {'★'.repeat(Math.round(rating))}{'☆'.repeat(5 - Math.round(rating))}
      <span style={{ color: '#7a7060', fontSize: size - 2, marginLeft: 4 }}>
        {parseFloat(rating).toFixed(1)}
      </span>
    </span>
  );
}

export default function History() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [pendingRatings, setPendingRatings] = useState([]);
  const [ratingOrder, setRatingOrder] = useState(null);
  const [rated, setRated] = useState(new Set());

  const load = async () => {
    try {
      const [txRes, ratingRes] = await Promise.all([
        api.get('/api/v1/transactions/'),
        api.get('/api/v1/ratings/pending').catch(() => ({ data: [] })),
      ]);
      setTransactions(Array.isArray(txRes.data) ? txRes.data : []);
      setPendingRatings(Array.isArray(ratingRes.data) ? ratingRes.data : []);
    } catch {
      setTransactions([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const pendingRefs = new Set(pendingRatings.map(r => r.ref));

  const filtered = transactions.filter(tx => {
    const matchFilter = filter === 'all' || tx.status === filter;
    const matchSearch = !search ||
      tx.recipient_name?.toLowerCase().includes(search.toLowerCase()) ||
      tx.ref?.toLowerCase().includes(search.toLowerCase()) ||
      tx.recipient_phone?.includes(search);
    return matchFilter && matchSearch;
  });

  // Totales
  const totalUsd = transactions
    .filter(t => t.status === 'completed')
    .reduce((s, t) => s + (parseFloat(t.amount_usd) || 0), 0);

  const totalCup = transactions
    .filter(t => t.status === 'completed')
    .reduce((s, t) => s + (parseFloat(t.cup_to_deliver) || 0), 0);

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontFamily: 'Georgia,serif', fontSize: 26, margin: '0 0 4px' }}>
          Mis remesas
        </h1>
        <p style={{ fontSize: 13, color: '#7a7060', margin: 0 }}>
          {transactions.length} órdenes
          {totalUsd > 0 && (
            <span style={{ marginLeft: 12 }}>
              · <b>${totalUsd.toFixed(0)}</b> enviados
              {totalCup > 0 && (
                <span style={{ color: '#2d6a4f' }}>
                  {' '}· <b>{(totalCup / 1000).toFixed(0)}K CUP</b> entregados
                </span>
              )}
            </span>
          )}
        </p>
      </div>

      {/* Banner calificaciones pendientes */}
      {pendingRatings.filter(r => !rated.has(r.ref)).length > 0 && (
        <div style={{ background: '#fef3e2', border: '1px solid #d97706', borderRadius: 12, padding: '14px 18px', marginBottom: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
          <div>
            <div style={{ fontWeight: 700, fontSize: 14, color: '#b45309' }}>
              ⭐ {pendingRatings.filter(r => !rated.has(r.ref)).length} servicio(s) sin calificar
            </div>
            <div style={{ fontSize: 12, color: '#b45309', marginTop: 2 }}>
              Tu opinión ayuda a mejorar el servicio
            </div>
          </div>
          <button
            onClick={() => setRatingOrder(pendingRatings.find(r => !rated.has(r.ref)))}
            style={{ padding: '8px 16px', borderRadius: 8, border: 'none', background: '#b45309', color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', whiteSpace: 'nowrap' }}>
            Calificar ahora →
          </button>
        </div>
      )}

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          style={{ padding: '9px 14px', border: '1px solid #e2dbd0', borderRadius: 8, fontSize: 13, outline: 'none', width: 220 }}
          placeholder="Buscar receptor o referencia..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
          {Object.entries(FILTER_LABELS).map(([key, label]) => (
            <button key={key} onClick={() => setFilter(key)} style={{
              padding: '7px 12px', borderRadius: 20, border: 'none', cursor: 'pointer',
              fontSize: 11, fontWeight: 600,
              background: filter === key ? '#c94a1e' : '#f0ebe2',
              color:      filter === key ? '#fff' : '#7a7060',
            }}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {/* Lista */}
      {loading ? (
        <div style={{ padding: 40, textAlign: 'center', color: '#7a7060' }}>Cargando...</div>
      ) : filtered.length === 0 ? (
        <div style={{ background: '#fff', borderRadius: 14, padding: 48, textAlign: 'center', border: '1px solid #e2dbd0' }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📋</div>
          <div style={{ fontSize: 15, color: '#7a7060' }}>
            {search || filter !== 'all' ? 'No hay resultados para esta búsqueda' : 'Aún no has enviado ninguna remesa'}
          </div>
        </div>
      ) : filtered.map((tx, i) => {
        const sc        = STATUS_COLORS[tx.status] || { bg: '#f0ebe2', color: '#7a7060', label: tx.status };
        const canRate   = tx.status === 'completed' && pendingRefs.has(tx.ref) && !rated.has(tx.ref);
        const pendingOrder = pendingRatings.find(r => r.ref === tx.ref);
        const cupAmount = parseFloat(tx.cup_to_deliver) || 0;

        return (
          <div key={i} style={{ background: '#fff', border: '1px solid #e2dbd0', borderRadius: 12, padding: '16px 18px', marginBottom: 8 }}>

            {/* Header */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
              <div>
                <div style={{ fontFamily: 'monospace', fontSize: 11, color: '#c94a1e', marginBottom: 2 }}>{tx.ref}</div>
                <div style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{tx.recipient_name}</div>
                <div style={{ fontSize: 12, color: '#7a7060', marginBottom: 6 }}>{tx.recipient_phone}</div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                  <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: sc.bg, color: sc.color }}>
                    {sc.label}
                  </span>
                  <span style={{ fontSize: 11, color: '#b0a090' }}>
                    {tx.created_at ? new Date(tx.created_at).toLocaleDateString('es-ES') : '—'}
                  </span>
                  {tx.delivery_method && (
                    <span style={{ fontSize: 11, color: '#7a7060' }}>
                      {DELIVERY_LABELS[tx.delivery_method] || tx.delivery_method}
                    </span>
                  )}
                </div>
              </div>

              {/* Montos */}
              <div style={{ textAlign: 'right', flexShrink: 0 }}>
                <div style={{ fontFamily: 'Georgia,serif', fontSize: 22, fontWeight: 700, marginBottom: 2 }}>
                  ${parseFloat(tx.amount_usd).toFixed(0)}
                </div>
                {cupAmount > 0 && (
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#2d6a4f' }}>
                    {cupAmount.toLocaleString()} CUP
                  </div>
                )}
                <div style={{ fontSize: 11, color: '#b0a090' }}>
                  fee ${parseFloat(tx.fee_usd || 0).toFixed(2)}
                </div>
              </div>
            </div>

            {/* Pago al agente — solo si tiene tx_hash */}
            {tx.agent_payment_tx && tx.agent_payment_status === 'paid' && (
              <div style={{ background: '#eaf4ef', borderRadius: 8, padding: '7px 12px', marginBottom: 8, fontSize: 11, color: '#2d6a4f', display: 'flex', justifyContent: 'space-between' }}>
                <span>✓ Agente pagado on-chain · {tx.agent_payment_asset} · {tx.agent_payment_net}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 10, color: '#7a7060' }}>
                  {tx.agent_payment_tx?.slice(0, 12)}...
                </span>
              </div>
            )}

            {/* Chat button */}
            {['in_delivery', 'completed'].includes(tx.status) && (
              <ChatButton orderRef={tx.ref} style={{ marginBottom: 8 }} />
            )}

            {/* Calificar */}
            {canRate && (
              <button
                onClick={() => setRatingOrder(pendingOrder || tx)}
                style={{ width: '100%', padding: '9px', borderRadius: 8, border: '1px solid #f59e0b', background: '#fffbeb', color: '#b45309', fontSize: 13, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                ⭐ Calificar este servicio
              </button>
            )}

            {rated.has(tx.ref) && (
              <div style={{ fontSize: 12, color: '#2d6a4f', marginTop: 4 }}>✓ Calificado</div>
            )}
          </div>
        );
      })}

      {/* Rating Modal */}
      {ratingOrder && (
        <RatingModal
          order={ratingOrder}
          onClose={() => setRatingOrder(null)}
          onSubmitted={(ref) => {
            setRated(prev => new Set([...prev, ref]));
            const nextPending = pendingRatings.find(r => r.ref !== ref && !rated.has(r.ref));
            if (nextPending) setTimeout(() => setRatingOrder(nextPending), 500);
          }}
        />
      )}
    </div>
  );
}
